export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

// Lower number = higher priority (locality beats sublocality, etc.). A single
// Google address can contain multiple city-ish components — e.g. a Brooklyn
// address carries both `locality: New York` and `sublocality: Brooklyn` — and
// we want to commit the most specific "city" concept (`locality`) to the form.
const CITY_TYPE_PRIORITY: Record<string, number> = {
  locality: 0,
  sublocality: 1,
  sublocality_level_1: 2,
};

// Google Maps JS API address component shape. Defined locally (instead of
// importing the full `google.maps.places` namespace) so this module can be
// unit-tested without the Maps SDK loaded.
export interface AddressComponentLike {
  longText: string | null;
  shortText: string | null;
  types: string[];
}

// Scores a single address component's suitability as "the city". Lower value
// wins: a component tagged `locality` (rank 0) is preferred over one tagged
// `sublocality` (rank 1), so e.g. an NYC address picks "New York" over
// "Brooklyn". Returns `+Infinity` when the component carries no city-style
// tag at all, so callers can compare with `<` without special-casing. In
// practice a component has at most one city-style tag, but Google's schema
// doesn't forbid overlap so we defensively take the min.
function getCityCandidatePriority(types: readonly string[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (const t of types) {
    const priority = CITY_TYPE_PRIORITY[t];
    if (priority !== undefined && priority < best) best = priority;
  }
  return best;
}

export function parseAddressComponents(components: readonly AddressComponentLike[] | undefined): ParsedAddress {
  let streetNumber = '';
  let route = '';
  let state = '';
  let zip = '';
  let city = '';
  let bestCityPriority = Number.POSITIVE_INFINITY;

  // Each Google address component carries exactly one of the roles below; we
  // dispatch on the first match and `continue` so it's obvious at a glance
  // that the role assignments never fight each other. Anything that isn't one
  // of those four roles falls through to the city-candidate scoring.
  for (const c of components ?? []) {
    const types = new Set(c.types);

    if (types.has('street_number')) {
      streetNumber = c.longText ?? '';
      continue;
    }
    if (types.has('route')) {
      route = c.shortText ?? '';
      continue;
    }
    if (types.has('administrative_area_level_1')) {
      state = c.shortText ?? '';
      continue;
    }
    if (types.has('postal_code')) {
      zip = c.shortText ?? '';
      continue;
    }

    const priority = getCityCandidatePriority(c.types);
    if (priority < bestCityPriority) {
      bestCityPriority = priority;
      city = c.longText ?? '';
    }
  }

  return {
    street: `${streetNumber} ${route}`.trim(),
    city,
    state,
    zip,
  };
}
